import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

// Global design system constants
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const DeliveryPickupPolicy = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: NEUTRAL_BG }}>
      {/* Header */}
      <header className="py-4 px-6" style={{ backgroundColor: NEUTRAL_TEXT }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full mr-3" style={{ backgroundColor: PRIMARY_BROWN }} aria-hidden="true"></div>
            <span className="text-lg font-semibold text-white">Vesta • Legal Center</span>
          </div>
          <BackButton
            onClick={() => window.history.back()}
            variant="link"
            size="small"
            color="#C97B53"
            ariaLabel="Back to Legal Center"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="rounded-2xl shadow-lg p-8 mb-8" style={{ backgroundColor: NEUTRAL_WHITE }}>
          <h1 className="text-3xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
            Order, Delivery & Pickup Policy
          </h1>
          <p className="mb-4" style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
            Effective Date: [Insert Date]
          </p>
          <p style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
            This policy explains how orders on Vesta are fulfilled, the rules for delivery and pickup, and what buyers and sellers must do to ensure safe and timely transactions. It applies to all products and services sold on the Platform.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              1. Local Orders Only
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Vesta does not support shipping.</li>
              <li>All orders are fulfilled via local delivery or pickup.</li>
              <li>Service availability may vary by seller location and buyer address.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              2. Order Processing
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Sellers must confirm acceptance of an order promptly after purchase.</li>
              <li>Sellers must prepare orders in accordance with safe food handling standards.</li>
              <li>Any delay or inability to fulfill must be communicated to the buyer through Vesta's messaging system as soon as possible.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              3. Delivery Guidelines
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>Responsibility</h3>
                <p style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>Delivery can be handled by the seller, an authorized driver, or a third-party courier.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>Timing</h3>
                <p style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>Delivery must occur within the safe holding window for the product type (e.g., temperature control for perishable goods).</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>Proof of Handoff</h3>
                <p className="mb-3" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>Delivery completion requires one of the following:</p>
                <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                  <li>Photo of the delivered order at the drop-off location.</li>
                  <li>Buyer signature.</li>
                  <li>Delivery confirmation code entered by the buyer.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>Missed Deliveries</h3>
                <p className="mb-3" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>If the buyer is unavailable during the agreed window, the seller may:</p>
                <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                  <li>Attempt a redelivery (may incur an additional fee).</li>
                  <li>Require pickup at an alternate agreed location.</li>
                  <li>Cancel the order without refund if redelivery/pickup is not possible.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              4. Pickup Guidelines
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Sellers must provide the buyer with pickup location details and available time windows at checkout or promptly afterward.</li>
              <li>Buyers must arrive during the agreed pickup window.</li>
              <li>Sellers must hold orders for a reasonable grace period; after that, uncollected orders may be discarded without refund.</li>
              <li>Pickup locations must be clean, safe, and compliant with applicable regulations.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              5. Safe Handling Responsibilities
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Sellers are responsible for packaging and transporting food safely to the point of handoff.</li>
              <li>Buyers are responsible for safe handling and storage after receiving the order.</li>
              <li>Sellers must provide basic handling instructions (e.g., refrigeration needs) in the listing or at handoff.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              6. In-Home Chef & Catering Bookings
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Sellers providing in-home services must arrive at the agreed time and with required equipment.</li>
              <li>Buyers must ensure a clean, safe, and adequately equipped environment.</li>
              <li>Service completion should be confirmed in the Vesta system immediately after the event.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              7. Order Cancellations
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Cancellations are subject to the Seller's cancellation policy and may be non-refundable once preparation has begun.</li>
              <li>For preorders or catering, see the <Link to="/legal/preorder-custom" className="text-blue-600 hover:text-blue-500 underline">Preorder & Custom/Made-to-Order Policy</Link> for deposit and cancellation rules.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              8. Disputes
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>If there's a problem with an order, buyers should contact the seller first through Vesta messages.</li>
              <li>If unresolved within 48 hours, the issue can be escalated to Vesta for review.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              9. Policy Violations
            </h2>
            <p className="mb-3" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>Failure to comply with this policy may result in:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Listing removal.</li>
              <li>Account suspension or termination.</li>
              <li>Withholding of funds pending dispute resolution.</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              10. Delivery Fees & Charges
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Delivery fees are set by individual sellers and displayed at checkout.</li>
              <li>Additional charges for redelivery or special handling must be communicated in advance.</li>
              <li>All fees must be clearly disclosed before order confirmation.</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              11. Weather & Force Majeure
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>In cases of severe weather or other force majeure events, delivery/pickup may be delayed or rescheduled.</li>
              <li>Sellers must communicate delays promptly and work with buyers to find alternative arrangements.</li>
              <li>No penalties will apply for weather-related delays.</li>
            </ul>
          </section>

          {/* Section 12 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              12. Contact Information
            </h2>
            <p className="mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              For questions about this policy or to report violations, please contact us at:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              <li>Email: legal@vesta.com</li>
              <li>Support: support@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="rounded-xl shadow-md p-6" style={{ backgroundColor: NEUTRAL_WHITE }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
              13. Policy Updates
            </h2>
            <p style={{ color: NEUTRAL_TEXT, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              We may update this policy to reflect changes in law, industry practices, or Platform operations. Updates will be communicated via email or in-app notifications.
            </p>
          </section>

          {/* Final Statement */}
          <section className="rounded-xl p-6" style={{ backgroundColor: '#FFF3E0', border: '1px solid #FFE0B2' }}>
            <p className="text-center font-medium" style={{ color: '#FF9800', fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
              By using Vesta's delivery and pickup services, you acknowledge that you have read, understood, and agree to be bound by this Order, Delivery & Pickup Policy.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default DeliveryPickupPolicy; 